import React, { useEffect, useMemo, useReducer } from 'react';
import { Card, Table } from 'antd';
import produce from 'immer';
import _capitalize from 'lodash/capitalize';
import _flowRight from 'lodash/flowRight';
import _isEmpty from 'lodash/isEmpty';
import _join from 'lodash/join';
import _pickBy from 'lodash/pickBy';
import _startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';

import { useAuditLogList } from 'api/auditLogs';
import { DB_RECORD_DATETIME_FORMAT, MID_PAGE_SIZE } from 'appConstants';
import Error from 'containers/Analytics/components/Error';
import { TCustomerDetails } from 'containers/Customer/i18n/TModules';
import usePermissions from 'containers/Permissions/usePermissions';
import { useLocalization } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { EventEmitter } from 'utils/events';

import { buildAudienceSummary, isAudienceEntity, isAudienceLogUnavailable } from './audience';
import { hasAnyValue } from './changes';
import ChangesDetails from './components/ChangesDetail';

const format = _flowRight(_capitalize, _startCase);

export const renderAction = record => {
  return record.event === 'create' ? 'Created' : 'Updated';
};

export const filterEmptyChanges = data =>
  // filter out empty object values from 'changes' key in response.data
  data.reduce((acc, record) => {
    const r = { ...record };
    const filteredChanges = _pickBy(r.changes, hasAnyValue);
    if (!_isEmpty(filteredChanges)) {
      r.changes = filteredChanges;
      acc.push(r);
    }
    return acc;
  }, []);

interface AuditLogState {
  page: number;
  size: number;
}

interface AuditLogProps {
  id: string | number;
  entity: string;
  // PLA-9537: EventEmitter event that re-fetches the log after an admin action on the record
  refreshEvent?: string;
  extraParams?: object;
  isChanges2?: boolean;
  // PLA-9521: audience history is gated by the audiences permission, not audit_logs.
  permissionSection?: string;
}

const AuditLog = ({
  id,
  entity,
  extraParams,
  isChanges2 = true,
  refreshEvent,
  permissionSection = 'audit_logs'
}: AuditLogProps) => {
  const [params, paramsDispatch] = useReducer(
    (state: AuditLogState, action): AuditLogState =>
      produce(state, draft => {
        switch (action.type) {
          case 'TABLE':
            return void (draft.page = action.pagination);
        }
      }),
    {
      page: 1,
      size: MID_PAGE_SIZE,
      ...extraParams
    }
  );

  const { canView } = usePermissions(permissionSection);
  const { timezone } = useLocalization();

  const { t } = useMultiLanguageTranslation();

  const URL = isChanges2 ? `/v4/dash/changes2/${entity}/${id}` : `/v4/dash/changes/${entity}/${id}`;

  const { data, meta, isLoading, error, mutate, isValidating } = useAuditLogList(URL, params);
  const loading = isLoading || isValidating;
  const refetch = mutate;

  useEffect(() => {
    if (!refreshEvent) return undefined;
    return EventEmitter.subscribe(refreshEvent, () => mutate());
  }, [refreshEvent, mutate]);
  const response = data ? { data, meta } : undefined;

  const renderSummary = record => {
    if (isAudienceEntity(entity)) {
      return buildAudienceSummary(record);
    }
    if (record.event === 'create') {
      return `${format(entity)} ${t(`${TCustomerDetails}.AuditLog.RenderSummaryIsCreated`)}`;
    }
    // filter out entries with [null, null]
    const filteredChanges = _pickBy(record.changes, hasAnyValue);
    const summary = Object.keys(filteredChanges).map(
      k => `${format(k)} ${t(`${TCustomerDetails}.AuditLog.RenderSummaryIsUpdated`)}`
    );
    return _join(summary, ', ');
  };

  const renderChanges = record => <ChangesDetails changes={record.changes} entity={entity} />;

  const onTableChange = (pagination, filters, sorter) =>
    paramsDispatch({ type: 'TABLE', pagination: pagination.current, sorter });

  const dataSource = useMemo(() => {
    const filteredData = response?.data ? filterEmptyChanges(response.data) : [];

    const dataWithKeys = filteredData.map(item => ({ ...item, _id: uuid() }));

    return dataWithKeys;
  }, [response?.data]);

  if (!canView) return null;

  if (isAudienceLogUnavailable(entity, error)) return null;

  if (error) {
    return (
      <Card
        title={t(`${TCustomerDetails}.AuditLog.Title`)}
        style={{ marginTop: '1rem' }}
        loading={loading}
      >
        <Error retry={refetch} />
      </Card>
    );
  }

  return (
    <Card
      title={t(`${TCustomerDetails}.AuditLog.Title`)}
      style={{ marginTop: '1rem' }}
      loading={loading}
      data-testid="audit-log"
    >
      <Table
        rowKey="_id"
        onChange={onTableChange}
        loading={loading}
        pagination={{
          position: ['bottomRight'],
          current: params.page,
          defaultPageSize: MID_PAGE_SIZE,
          pageSize: params.size,
          total: response?.meta?.total_count || 0
        }}
        dataSource={dataSource}
        columns={[
          {
            title: t(`${TCustomerDetails}.AuditLog.LastUpdatedAt`),
            dataIndex: 'changed_at',
            key: 'changed_at',
            render: (_, record) =>
              moment(record.changed_at).tz(timezone).format(DB_RECORD_DATETIME_FORMAT)
          },
          {
            title: t(`${TCustomerDetails}.AuditLog.IpAddress`),
            dataIndex: 'remote_ip',
            key: 'remote_ip'
          },
          {
            title: t(`${TCustomerDetails}.AuditLog.User`),
            dataIndex: 'user_email',
            key: 'user_email'
          },
          {
            title: t(`${TCustomerDetails}.AuditLog.Action`),
            key: 'action',
            render: (_, record) => renderAction(record)
          },
          {
            title: t(`${TCustomerDetails}.AuditLog.Description`),
            dataIndex: 'changes',
            key: 'changes',
            render: (_, record) => renderSummary(record)
          }
        ]}
        expandRowByClick
        expandable={{
          expandedRowRender: renderChanges
        }}
      />
    </Card>
  );
};

export default AuditLog;
